/**
 * Phase INSREQ-IMP — Insurance Requirements (Frozen) card for the
 * admin rental detail page.
 *
 * Reads:
 *   - ``rental.coi_requirements_snapshot``  (Phase INSREQ-IMP — new
 *      rental-level snapshot field; auto-frozen at import time, also
 *      re-frozen on every COI-send and on manual freeze).
 *   - Legacy fallback: ``contract.coi_requirements_snapshot`` (the
 *      old contract-level snapshot, set at digital signing time) —
 *      surfaced when the rental-level field hasn't been written yet
 *      (e.g. a pre-Phase-INSREQ-IMP digitally-signed rental).
 *
 * Writes (super_admin or admin):
 *   POST /api/admin/rentals/{rental_id}/insurance-snapshot/freeze
 *     body: { template_id?: str, overwrite?: bool }
 *
 * Display branches (per user decisions):
 *   1. Non-imported, no snapshot ⇒ keep legacy copy
 *      ("Snapshot will be created when the contract is sent for signature.")
 *   2. Imported, no snapshot, NO active templates ⇒ explainer + disabled
 *      "Freeze snapshot now" button.
 *   3. Imported, no snapshot, templates exist ⇒ enabled "Freeze snapshot
 *      now" using system default (button kicks the API).
 *   4. Imported, with snapshot ⇒ full content + "Freeze snapshot now"
 *      (with overwrite-confirm) + Re-freeze ▼ dropdown.
 *   5. Non-imported, with snapshot ⇒ full content + "Locked at contract
 *      signature" chip. Super-admins also see an Override ▼ dropdown.
 */
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    ShieldCheck, Lock, Snowflake, ChevronDown, ChevronRight, Loader2,
} from "lucide-react";

function Term({ label, value }) {
    return (
        <div>
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-slate-900 text-sm font-medium">{value}</p>
        </div>
    );
}

function fmtFrozen(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const SOURCE_LABEL = {
    auto_import: "auto_import",
    manual_freeze: "manual_freeze",
    manual_admin: "manual_admin",  // legacy rows pre-rename
    coi_send: "coi_send",
};

export default function InsuranceSnapshotCard({
    rental, contract, coiTemplates, isSuperAdmin, onRefreshed,
}) {
    const [busy, setBusy] = useState(false);
    // For Re-freeze ▼ template-pick confirm.
    const [confirm, setConfirm] = useState(null); // { template_id, template_name }
    // For "Freeze snapshot now" overwrite confirm (uses system default).
    const [confirmOverwrite, setConfirmOverwrite] = useState(false);
    // Phase RENTAL-COLLAPSE — default EXPANDED (admins often need to
    // eyeball the frozen limits before releasing equipment).
    const COLLAPSE_KEY = "admin.rentalDetail.insuranceRequirementsCollapsed";
    const [collapsed, setCollapsed] = useState(() => {
        try { return (localStorage.getItem(COLLAPSE_KEY) ?? "0") === "1"; }
        catch { return false; }
    });
    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            const next = !prev;
            try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* noop */ }
            return next;
        });
    };
    const onHeaderKey = (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleCollapsed(); }
    };

    // Prefer the rental-level field (Phase INSREQ-IMP); fall back to the
    // legacy contract-level snapshot for digitally-signed rentals that
    // pre-date this phase.
    const rentalSnap = rental?.coi_requirements_snapshot || null;
    const legacySnap = !rentalSnap && contract?.coi_requirements_snapshot
        ? contract.coi_requirements_snapshot : null;
    const snap = rentalSnap || legacySnap;
    const imported = !!rental?.imported;
    const activeTemplates = (coiTemplates || []).filter((t) => !t.archived);
    const hasTemplates = activeTemplates.length > 0;

    // For the imported "with snapshot" path admins (or super_admin) can
    // re-freeze. For the digitally-signed "with snapshot" path the
    // snapshot is locked at signature unless the actor is a super_admin
    // override.
    const canRefreeze = !!snap && (imported || isSuperAdmin);

    // ---------------------------------------------------------------
    // Mutation — POST .../insurance-snapshot/freeze.
    // ``overwrite`` is sent as true whenever a snapshot is already
    // on file; that's the server-side guard for unintended clobbers
    // (the UI confirms first).
    async function performFreeze(templateId, { isOverwrite = false } = {}) {
        setBusy(true);
        try {
            const payload = {};
            if (templateId) payload.template_id = templateId;
            if (isOverwrite) payload.overwrite = true;
            const { data } = await api.post(
                `/admin/rentals/${rental.id}/insurance-snapshot/freeze`,
                payload,
            );
            toast.success(snap ? "Insurance snapshot re-frozen" : "Insurance snapshot frozen");
            if (onRefreshed) onRefreshed(data?.snapshot);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
            setConfirm(null);
            setConfirmOverwrite(false);
        }
    }

    // "Freeze snapshot now" click handler — when the rental already
    // has a snapshot we open the overwrite confirm; otherwise we fire
    // straight away with the system default template.
    function onFreezeNowClick() {
        if (snap) {
            setConfirmOverwrite(true);
        } else {
            performFreeze(null, { isOverwrite: false });
        }
    }

    // ---------------------------------------------------------------
    // Render branches.

    // (1) Non-imported, no snapshot → legacy copy.
    if (!snap && !imported) {
        return (
            <Card>
                <CardContent className="p-5 space-y-3" data-testid="rental-insurance-requirements-card">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">
                            Insurance requirements (frozen)
                        </p>
                    </div>
                    <p className="text-sm text-slate-500" data-testid="rental-insurance-snapshot-empty">
                        Snapshot will be created when the contract is sent for signature.
                    </p>
                </CardContent>
            </Card>
        );
    }

    // (2 + 3) Imported, no snapshot.
    if (!snap && imported) {
        return (
            <Card>
                <CardContent className="p-5 space-y-3" data-testid="rental-insurance-requirements-card">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">
                            Insurance requirements (frozen)
                        </p>
                    </div>
                    <p className="text-sm text-slate-600" data-testid="rental-insurance-snapshot-imported-empty">
                        {hasTemplates
                            ? "Imported rentals don't have a contract-send step. Click \u201CFreeze snapshot now\u201D to capture the current insurance requirements as the legal baseline for this rental."
                            : "No insurance requirements template available. Add one in Settings → COI Requirements Templates, then freeze the snapshot here."}
                    </p>
                    <div>
                        <Button
                            size="sm"
                            disabled={!hasTemplates || busy}
                            onClick={() => performFreeze(null, { isOverwrite: false })}
                            className="gap-1.5 bg-blue-900 hover:bg-blue-800"
                            data-testid="rental-insurance-snapshot-freeze-btn"
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Snowflake size={13} />}
                            Freeze snapshot now
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // (4 + 5) — snapshot present.
    const isLegacy = !rentalSnap && !!legacySnap;
    const sourceLabel = isLegacy ? "contract_signature" : (SOURCE_LABEL[snap.source] || snap.source || "—");
    const templateName = snap.template_name || (snap.coverages || {}).name;
    const coverages = snap.coverages || snap; // legacy contract snapshots have flat fields
    const pdSource = snap.physical_damage_limit_source;

    return (
        <Card>
            <CardContent className="p-5 space-y-3" data-testid="rental-insurance-requirements-card">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div
                        role="button" tabIndex={0}
                        aria-expanded={!collapsed}
                        onClick={toggleCollapsed} onKeyDown={onHeaderKey}
                        className="flex items-center gap-2 cursor-pointer select-none"
                        data-testid="rental-insurance-snapshot-toggle"
                    >
                        {collapsed ? (
                            <ChevronRight size={16} className="text-slate-500 shrink-0" />
                        ) : (
                            <ChevronDown size={16} className="text-slate-500 shrink-0" />
                        )}
                        <ShieldCheck size={15} className="text-blue-900" />
                        <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">
                            Insurance requirements (frozen)
                        </p>
                    </div>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="flex items-center gap-2"
                    >
                        {!imported && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 px-2 py-0.5 text-[10px] font-semibold"
                                data-testid="rental-insurance-snapshot-locked-chip">
                                <Lock size={9} /> Locked at contract signature
                            </span>
                        )}
                        {imported && (
                            <Button
                                size="sm"
                                disabled={busy || !hasTemplates}
                                onClick={onFreezeNowClick}
                                className="gap-1.5 bg-blue-900 hover:bg-blue-800"
                                data-testid="rental-insurance-snapshot-freeze-now-btn"
                            >
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Snowflake size={13} />}
                                Freeze snapshot now
                            </Button>
                        )}
                        {canRefreeze && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={busy || !hasTemplates}
                                        className="gap-1.5"
                                        data-testid="rental-insurance-snapshot-refreeze-trigger"
                                    >
                                        {imported ? "Re-freeze" : "Override"}
                                        <ChevronDown size={12} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64"
                                    data-testid="rental-insurance-snapshot-refreeze-menu">
                                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-500">
                                        Re-freeze using template…
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {activeTemplates.map((t) => (
                                        <DropdownMenuItem
                                            key={t.id}
                                            onClick={() => setConfirm({ template_id: t.id, template_name: t.name })}
                                            data-testid={`rental-insurance-snapshot-refreeze-opt-${t.id}`}
                                            className="flex flex-col items-start"
                                        >
                                            <span className="text-sm font-medium text-slate-900">{t.name}</span>
                                            {t.is_default && (
                                                <span className="text-[10px] text-blue-900 font-semibold uppercase tracking-wide">Default</span>
                                            )}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </div>

                {!collapsed && (
                <>
                <p className="text-xs text-slate-600" data-testid="rental-insurance-snapshot-meta">
                    Frozen <span className="font-medium text-slate-800">{fmtFrozen(snap.frozen_at)}</span>
                    {templateName && (
                        <>
                            {" "}from <span className="font-medium text-slate-800">&ldquo;{templateName}&rdquo;</span>
                        </>
                    )}
                    {" "}
                    <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 px-1.5 py-0.5 ml-1 text-[10px] font-semibold"
                        data-testid="rental-insurance-snapshot-source-chip">
                        Snapshot source: {sourceLabel}
                    </span>
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <Term label="General liability" value={coverages.general_liability_limit || "—"} />
                    <Term label="Auto liability" value={coverages.auto_liability_limit || "—"} />
                    <Term
                        label="Physical damage"
                        value={
                            <span data-testid="rental-snapshot-physical-damage" className="inline-flex items-center gap-1.5">
                                <span>{snap.physical_damage_limit || coverages.physical_damage_limit || "—"}</span>
                                {pdSource && (
                                    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${
                                        pdSource === "rental_value"
                                            ? "bg-emerald-50 text-emerald-900 ring-emerald-200"
                                            : "bg-slate-100 text-slate-700 ring-slate-200"
                                    }`}
                                    data-testid="rental-snapshot-physical-damage-source">
                                        {pdSource === "rental_value" ? "from rental value" : "from template"}
                                    </span>
                                )}
                            </span>
                        }
                    />
                    <Term label="Waiver of subrogation" value={coverages.waiver_of_subrogation ? "Yes" : "No"} />
                    <Term label="Notice of cancellation" value={`${coverages.notice_of_cancellation_days ?? 30} days`} />
                    <Term
                        label="Certificate holder"
                        value={
                            <span>
                                {coverages.certificate_holder_name || "—"}
                                {coverages.certificate_holder_address && (
                                    <span className="block text-xs text-slate-500 whitespace-pre-line">
                                        {coverages.certificate_holder_address}
                                    </span>
                                )}
                            </span>
                        }
                    />
                    <div className="col-span-1 md:col-span-2 border-t border-slate-100 pt-2 mt-1">
                        <p className="text-xs text-slate-500">Additional insureds</p>
                        <p className="text-slate-900 text-sm">
                            {(coverages.additional_insured && coverages.additional_insured.length > 0)
                                ? coverages.additional_insured.join(", ") : "—"}
                        </p>
                    </div>
                    <div className="col-span-1 md:col-span-2">
                        <p className="text-xs text-slate-500">Other requirements</p>
                        <p className="text-slate-900 text-sm whitespace-pre-line">{coverages.other_requirements || "—"}</p>
                    </div>
                </div>
                </>
                )}

                {/* Re-freeze ▼ template-pick confirm. */}
                <Dialog open={!!confirm}
                    onOpenChange={(o) => { if (!o) setConfirm(null); }}>
                    <DialogContent data-testid="rental-insurance-snapshot-refreeze-confirm">
                        <DialogHeader>
                            <DialogTitle>
                                Re-freeze insurance snapshot using &ldquo;{confirm?.template_name}&rdquo;?
                            </DialogTitle>
                            <DialogDescription>
                                Prior snapshot will be archived in history.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="ghost"
                                disabled={busy}
                                onClick={() => setConfirm(null)}
                                data-testid="rental-insurance-snapshot-refreeze-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={busy}
                                onClick={() => performFreeze(confirm?.template_id, { isOverwrite: true })}
                                className="gap-1.5 bg-blue-900 hover:bg-blue-800"
                                data-testid="rental-insurance-snapshot-refreeze-confirm-btn"
                            >
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Snowflake size={13} />}
                                Re-freeze
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* "Freeze snapshot now" — overwrite confirm for imported. */}
                <Dialog open={confirmOverwrite}
                    onOpenChange={(o) => { if (!o) setConfirmOverwrite(false); }}>
                    <DialogContent data-testid="rental-insurance-snapshot-overwrite-confirm">
                        <DialogHeader>
                            <DialogTitle>Re-freeze insurance snapshot?</DialogTitle>
                            <DialogDescription>
                                This rental already has a frozen snapshot
                                (source: <span className="font-mono">{sourceLabel}</span>).
                                Re-freezing will capture the current default template
                                as the new baseline; the prior snapshot is archived in history.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="ghost"
                                disabled={busy}
                                onClick={() => setConfirmOverwrite(false)}
                                data-testid="rental-insurance-snapshot-overwrite-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={busy}
                                onClick={() => performFreeze(null, { isOverwrite: true })}
                                className="gap-1.5 bg-blue-900 hover:bg-blue-800"
                                data-testid="rental-insurance-snapshot-overwrite-confirm-btn"
                            >
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Snowflake size={13} />}
                                Re-freeze now
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
}
